import type { FlowNodeData } from "../types/flow.types";

/**
 * Deep equality for plain JSON data (no functions, Dates, Maps — FlowNodeData
 * snapshots arrive over the wire as plain objects/arrays/primitives).
 */
function deepJsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => deepJsonEqual(item, b[i]));
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => deepJsonEqual(aRecord[key], bRecord[key]));
}

/**
 * Content equality for live-run node snapshots.
 *
 * Every WebSocket message rebuilds the nodes array (`snapshotToNodes`), so a
 * content-identical update still carries a fresh identity. Store writers use
 * this to preserve the previous `currentNodes` reference when nothing actually
 * changed — otherwise every subscriber (and React's useSyncExternalStore
 * consistency check) re-renders per message, which under a rapid stream
 * escalates into "Maximum update depth exceeded".
 */
export function flowNodesEqual(
  a: readonly FlowNodeData[],
  b: readonly FlowNodeData[],
): boolean {
  return a === b || deepJsonEqual(a, b);
}
